/**
 * Bonded design-system primitives, ported to inline styles for the research lab.
 *
 * Values mirror bonded-design-system/tokens/*.css exactly: 3px ink outline, pill
 * or generous radius, hard "sticker" shadow with no blur, bounce motion, hover
 * lifts up-left, press pushes down-right.
 *
 * This is a port rather than an import because that directory is a read-only
 * generated export whose components are one-line re-exports of a `window.BondedDS`
 * IIFE bundle — not importable by Vite. apps/web-to-app carries an equivalent
 * port, and the duplication is deliberate: neither app may depend on the other.
 *
 * Everything visual reads a CSS custom property from index.css. No raw hex, and
 * no Tailwind colour or spacing utilities.
 */
import { useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react'

function useSticker() {
	const [hover, setHover] = useState(false)
	const [press, setPress] = useState(false)
	const handlers = {
		onMouseEnter: () => setHover(true),
		onMouseLeave: () => {
			setHover(false)
			setPress(false)
		},
		onMouseDown: () => setPress(true),
		onMouseUp: () => setPress(false),
		onTouchStart: () => setPress(true),
		onTouchEnd: () => setPress(false),
	}
	return { hover, press, handlers }
}

// ---- Button -----------------------------------------------------------------

type ButtonVariant = 'primary' | 'accent' | 'sunshine' | 'cloud'
type ButtonSize = 'sm' | 'md' | 'lg'

const BTN_BG: Record<ButtonVariant, string> = {
	primary: 'var(--action-primary)',
	accent: 'var(--action-accent)',
	sunshine: 'var(--sunshine)',
	cloud: 'var(--cloud)',
}
const BTN_FG: Record<ButtonVariant, string> = {
	primary: 'var(--action-primary-label)',
	accent: 'var(--action-accent-label)',
	sunshine: 'var(--ink)',
	cloud: 'var(--ink)',
}
const BTN_PAD: Record<ButtonSize, string> = { sm: '10px 18px', md: '14px 26px', lg: '18px 34px' }
const BTN_FONT: Record<ButtonSize, string> = { sm: '15px', md: '17px', lg: '20px' }

export function Button({
	variant = 'primary',
	size = 'md',
	block = false,
	disabled,
	loading,
	onClick,
	children,
	style,
	type = 'button',
	href,
	target,
	rel,
}: {
	variant?: ButtonVariant
	size?: ButtonSize
	block?: boolean
	disabled?: boolean
	loading?: boolean
	/**
	 * The event argument exists for the `href` case, where the landing page
	 * intercepts a plain left click to keep navigation client-side. Every survey
	 * call site passes a zero-argument handler, which stays assignable.
	 */
	onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void
	children: ReactNode
	style?: CSSProperties
	type?: 'button' | 'submit'
	/**
	 * Renders an <a> instead of a <button>, styled identically.
	 *
	 * A control that navigates should be a link: it has to survive middle-click,
	 * right-click → open in new tab, and a crawler following it. The marketing
	 * landing page's two CTAs are the only call sites; everything in the survey
	 * omits `href` and gets the <button> it always had.
	 */
	href?: string
	target?: string
	rel?: string
}) {
	const { hover, press, handlers } = useSticker()
	const isDisabled = disabled || loading
	const shadow = press ? 'none' : hover ? 'var(--shadow-sticker-lg)' : 'var(--shadow-sticker)'
	const transform = press ? 'translate(4px,4px)' : hover ? 'translate(-2px,-2px)' : 'none'

	const sharedStyle: CSSProperties = {
		fontFamily: 'var(--font-display)',
		fontWeight: 700,
		fontSize: BTN_FONT[size],
		lineHeight: 1,
		padding: BTN_PAD[size],
		width: block ? '100%' : undefined,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 10,
		color: BTN_FG[variant],
		background: BTN_BG[variant],
		border: '3px solid var(--ink)',
		borderRadius: 'var(--radius-pill)',
		cursor: isDisabled ? 'not-allowed' : 'pointer',
		opacity: isDisabled ? 0.45 : 1,
		boxShadow: isDisabled ? 'var(--shadow-sticker)' : shadow,
		transform: isDisabled ? 'none' : transform,
		transition: 'transform var(--dur-quick) var(--ease-bounce), box-shadow var(--dur-quick) var(--ease-bounce)',
		...style,
	}

	if (href !== undefined) {
		return (
			<a
				href={href}
				target={target}
				rel={rel}
				onClick={onClick}
				{...handlers}
				style={{ ...sharedStyle, textDecoration: 'none' }}
			>
				{children}
			</a>
		)
	}

	return (
		<button type={type} disabled={isDisabled} onClick={onClick} {...(isDisabled ? {} : handlers)} style={sharedStyle}>
			{loading ? '…' : children}
		</button>
	)
}

// ---- OptionChip (single- or multi-select answer) -----------------------------

export function OptionChip({
	label,
	hint,
	emoji,
	selected,
	multi,
	disabled,
	onClick,
}: {
	label: string
	hint?: string
	/** Decorative only, and aria-hidden: the text label carries the meaning. */
	emoji?: string
	selected?: boolean
	multi?: boolean
	disabled?: boolean
	onClick: () => void
}) {
	const { hover, handlers } = useSticker()
	const active = selected || false
	return (
		<button
			onClick={onClick}
			disabled={disabled}
			// aria-pressed rather than a role=radio group: each option is an
			// independent toggle in the multi case, and a pressed button reads
			// correctly in both.
			aria-pressed={active}
			{...(disabled ? {} : handlers)}
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: 12,
				width: '100%',
				minHeight: 58,
				padding: '13px 18px',
				textAlign: 'left',
				fontFamily: 'var(--font-ui)',
				fontWeight: 800,
				fontSize: '1rem',
				color: 'var(--ink)',
				background: active ? 'var(--sunshine)' : 'var(--cloud)',
				border: '3px solid var(--ink)',
				borderRadius: 'var(--radius-md)',
				boxShadow: active || hover ? 'var(--shadow-hover)' : 'var(--shadow-sticker)',
				transform: active || hover ? 'translate(1px,1px)' : 'none',
				cursor: disabled ? 'not-allowed' : 'pointer',
				opacity: disabled && !active ? 0.6 : 1,
				transition: 'transform var(--dur-quick) var(--ease-soft), background var(--dur-quick) var(--ease-soft)',
			}}
		>
			<span
				aria-hidden
				style={{
					flex: '0 0 auto',
					display: 'inline-grid',
					placeItems: 'center',
					width: 24,
					height: 24,
					borderRadius: multi ? 6 : '50%',
					border: '3px solid var(--ink)',
					background: active ? 'var(--bubblegum-deep)' : 'var(--cloud)',
					boxShadow: active && !multi ? 'inset 0 0 0 4px var(--cloud)' : undefined,
					fontSize: '.8rem',
					fontWeight: 900,
					color: 'var(--ink)',
				}}
			>
				{active && multi ? '✓' : ''}
			</span>
			{emoji ? (
				<span aria-hidden style={{ flex: '0 0 auto', fontSize: '1.3rem', lineHeight: 1 }}>
					{emoji}
				</span>
			) : null}
			<span style={{ flex: 1 }}>
				{label}
				{hint ? (
					<span style={{ display: 'block', fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '.8rem', color: 'var(--text-muted)' }}>
						{hint}
					</span>
				) : null}
			</span>
		</button>
	)
}

// ---- OptionTile (visual grid choice) ----------------------------------------

export function OptionTile({
	label,
	emoji,
	selected,
	onClick,
}: {
	label: string
	/** Decorative only, and aria-hidden: the text label carries the meaning. */
	emoji?: string
	selected?: boolean
	onClick: () => void
}) {
	const { hover, press, handlers } = useSticker()
	return (
		<button
			onClick={onClick}
			aria-pressed={selected || false}
			{...handlers}
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				gap: 6,
				minHeight: 100,
				padding: '16px 12px',
				textAlign: 'center',
				fontFamily: 'var(--font-ui)',
				fontWeight: 800,
				fontSize: '1.02rem',
				color: 'var(--ink)',
				background: selected ? 'var(--sunshine)' : 'var(--cloud)',
				border: '3px solid var(--ink)',
				borderRadius: 'var(--radius-lg)',
				boxShadow: press ? 'none' : hover || selected ? 'var(--shadow-sticker-lg)' : 'var(--shadow-sticker)',
				transform: press ? 'translate(4px,4px)' : hover ? 'translate(-2px,-2px)' : 'none',
				cursor: 'pointer',
				transition: 'transform var(--dur-quick) var(--ease-bounce), box-shadow var(--dur-quick) var(--ease-bounce)',
			}}
		>
			{emoji ? (
				<span aria-hidden style={{ fontSize: '1.9rem', lineHeight: 1 }}>
					{emoji}
				</span>
			) : null}
			<span>{label}</span>
		</button>
	)
}

// ---- ScaleOption (one point on a Likert item) -------------------------------

/**
 * A single point on a rating scale.
 *
 * Radio semantics, not a slider: a slider starts at some value nobody chose, and
 * a dragged thumb hides which point is selected from assistive technology.
 *
 * Two layouts, chosen by whether the question supplies per-point anchors:
 *
 *  - `text` given → a full-width row with the anchor spelled out beside a mark.
 *    This is what a reproduced instrument needs. The UCLA short form's middle
 *    point is "Some of the time"; rendering a bare "2" and naming only the two
 *    ends asks a different question from the published one, and five anchors as
 *    long as "Neither agree nor disagree" cannot fit a horizontal row on a
 *    phone at any readable size.
 *  - no `text` → the compact numbered cell, with the ends named beside the row.
 *
 * Either way this renders a number the respondent picks. It never displays a
 * number we computed about them — there is no scoring anywhere in this app.
 */
export function ScaleOption({
	value,
	selected,
	label,
	text,
	onClick,
}: {
	value: number
	selected?: boolean
	/** Accessible name — the anchor when there is one, else e.g. "3 of 5". */
	label: string
	/** The visible anchor for this point, when the question names all of them. */
	text?: string
	onClick: () => void
}) {
	const { hover, press, handlers } = useSticker()
	const sticker = {
		color: 'var(--ink)',
		background: selected ? 'var(--sunshine)' : 'var(--cloud)',
		border: '3px solid var(--ink)',
		borderRadius: 'var(--radius-md)',
		boxShadow: press ? 'none' : hover || selected ? 'var(--shadow-sticker-lg)' : 'var(--shadow-sticker)',
		transform: press ? 'translate(3px,3px)' : hover ? 'translate(-2px,-2px)' : 'none',
		cursor: 'pointer',
		transition: 'transform var(--dur-quick) var(--ease-bounce), box-shadow var(--dur-quick) var(--ease-bounce)',
	} as const

	if (text) {
		return (
			<button
				role="radio"
				aria-checked={selected || false}
				aria-label={label}
				onClick={onClick}
				{...handlers}
				style={{
					...sticker,
					width: '100%',
					minHeight: 56,
					display: 'flex',
					alignItems: 'center',
					gap: 14,
					padding: '12px 16px',
					textAlign: 'left',
					fontFamily: 'var(--font-body)',
					fontWeight: 700,
					fontSize: '1rem',
				}}
			>
				{/* Decorative: the button's aria-label already names the anchor, so a
				    screen reader must not also announce this ring. */}
				<span
					aria-hidden="true"
					style={{
						flex: '0 0 auto',
						width: 22,
						height: 22,
						borderRadius: '50%',
						border: '3px solid var(--ink)',
						background: selected ? 'var(--ink)' : 'transparent',
					}}
				/>
				<span>{text}</span>
			</button>
		)
	}

	return (
		<button
			role="radio"
			aria-checked={selected || false}
			aria-label={label}
			onClick={onClick}
			{...handlers}
			style={{
				...sticker,
				width: '100%',
				minHeight: 62,
				display: 'grid',
				placeItems: 'center',
				fontFamily: 'var(--font-display)',
				fontWeight: 800,
				fontSize: '1.25rem',
			}}
		>
			{value}
		</button>
	)
}

// ---- Checkbox ---------------------------------------------------------------

export function Checkbox({
	checked,
	onChange,
	label,
	hint,
}: {
	checked: boolean
	onChange: (checked: boolean) => void
	label: string
	hint?: string
}) {
	return (
		<label
			style={{
				display: 'flex',
				alignItems: 'flex-start',
				gap: 10,
				cursor: 'pointer',
				textAlign: 'left',
				fontFamily: 'var(--font-body)',
				fontSize: '.85rem',
				color: 'var(--text-muted)',
				lineHeight: 1.45,
			}}
		>
			<input
				type="checkbox"
				checked={checked}
				onChange={(event) => onChange(event.target.checked)}
				style={{ flex: '0 0 auto', width: 20, height: 20, marginTop: 1, accentColor: 'var(--bubblegum-deep)', cursor: 'pointer' }}
			/>
			<span>
				<span style={{ fontWeight: 700, color: 'var(--ink)' }}>{label}</span>
				{hint ? <span style={{ display: 'block' }}>{hint}</span> : null}
			</span>
		</label>
	)
}

// ---- Card -------------------------------------------------------------------

export function Card({
	children,
	accent = 'var(--cloud)',
	padded = true,
	style,
}: {
	children: ReactNode
	accent?: string
	padded?: boolean
	style?: CSSProperties
}) {
	return (
		<div
			style={{
				background: accent,
				border: '3px solid var(--ink)',
				borderRadius: 'var(--radius-xl)',
				boxShadow: 'var(--shadow-sticker)',
				padding: padded ? 'var(--space-5)' : 0,
				display: 'flex',
				flexDirection: 'column',
				gap: 'var(--space-3)',
				...style,
			}}
		>
			{children}
		</div>
	)
}

// ---- ProgressBar ------------------------------------------------------------

/**
 * The run's progress. `accent` is the current section's colour, so the bar
 * changes hue as the survey moves between sections — the cheapest possible
 * signal that you have crossed into a new part of the questionnaire.
 */
export function ProgressBar({ value, accent = '--bubblegum-deep' }: { value: number; accent?: string }) {
	return (
		<div
			role="progressbar"
			aria-valuenow={Math.round(value)}
			aria-valuemin={0}
			aria-valuemax={100}
			style={{
				width: 'min(88%,680px)',
				height: 10,
				margin: '0 auto',
				overflow: 'hidden',
				border: '2px solid var(--ink)',
				borderRadius: 'var(--radius-pill)',
				background: 'var(--cloud)',
			}}
		>
			<span
				style={{
					display: 'block',
					height: '100%',
					width: `${Math.max(0, Math.min(100, value))}%`,
					borderRadius: 'var(--radius-pill)',
					background: `var(${accent})`,
					transition: 'width var(--dur-normal) var(--ease-soft), background var(--dur-normal) var(--ease-soft)',
				}}
			/>
		</div>
	)
}

// ---- Illustration -----------------------------------------------------------

/**
 * A brand illustration on a framing screen.
 *
 * Framed like every other card in this app — 3px ink border, radius, hard
 * sticker shadow — via `.illustration-frame` in index.css, so the landing hero
 * and every section-intro illustration get the identical treatment from one
 * place rather than each screen having to remember to wrap it. (This used to be
 * bare `<img>` styling with the frame bolted on separately for the landing
 * screen only via a one-off `.hero-badge` class — every other illustration was
 * unbordered as a result. Fixed by moving the frame in here.)
 *
 * Purely decorative, so `alt=""` and `aria-hidden` — the screen's heading and
 * body already say everything the picture is there to soften. Height capped in
 * the CSS (`vh` and `px` both) so it shrinks rather than pushing the Continue
 * button off a short phone screen.
 */
export function Illustration({ src }: { src: string }) {
	return (
		<div className="illustration-frame">
			<img src={src} alt="" aria-hidden loading="eager" decoding="async" />
		</div>
	)
}

// ---- Input ------------------------------------------------------------------

export function Input({
	label,
	value,
	onChange,
	type = 'text',
	placeholder,
	error,
	inputMode,
	autoComplete,
}: {
	label: string
	value: string
	onChange: (value: string) => void
	type?: string
	placeholder?: string
	error?: string | null
	inputMode?: 'email' | 'text'
	autoComplete?: string
}) {
	return (
		<label className="field">
			<span>{label}</span>
			<input
				type={type}
				value={value}
				placeholder={placeholder}
				inputMode={inputMode}
				autoComplete={autoComplete}
				aria-invalid={error ? true : undefined}
				onChange={(event) => onChange(event.target.value)}
			/>
			{error ? <span className="error-text">{error}</span> : null}
		</label>
	)
}

// ---- Textarea ---------------------------------------------------------------

/**
 * A free-text field, with a live remaining-character count.
 *
 * `maxLength` is a courtesy, not the control: the server validates the length
 * against the question's own cap and a CHECK constraint caps the column. Three
 * independent limits, because these are the highest-risk fields in the
 * instrument.
 */
export function Textarea({
	label,
	value,
	onChange,
	maxLength,
	placeholder,
	error,
	hint,
}: {
	label: string
	value: string
	onChange: (value: string) => void
	maxLength: number
	placeholder?: string
	error?: string | null
	hint?: string
}) {
	const remaining = maxLength - value.length
	return (
		<label className="field">
			<span>{label}</span>
			{hint ? <span className="question-help">{hint}</span> : null}
			<textarea
				value={value}
				placeholder={placeholder}
				maxLength={maxLength}
				aria-invalid={error ? true : undefined}
				onChange={(event) => onChange(event.target.value)}
			/>
			<span className={remaining < 0 ? 'char-count over' : 'char-count'} aria-live="polite">
				{remaining}
			</span>
			{error ? <span className="error-text">{error}</span> : null}
		</label>
	)
}
